import { useState } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import MobileCallButton from '../../components/feature/MobileCallButton';

export default function ContactPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleScroll = () => {
    setIsScrolled(window.scrollY > 20);
  };

  useState(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) {
      setError('Message must be 500 characters or less.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
      });
      const res = await fetch('https://readdy.ai/api/form/d6g6nl1ghdq4qda702r0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setError('Something went wrong. Please try again or call us directly.');
      }
    } catch {
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const services = [
    'Roof Replacement',
    'Roof Repair',
    'Storm Damage Restoration',
    'Siding Installation',
    'Gutter Installation',
    'Free Inspection',
    'Other',
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Navigation isScrolled={isScrolled} />

      {/* Hero Banner */}
      <div className="relative pt-32 pb-20 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://public.readdy.ai/ai/img_res/070ad5672dce3105eb040143f1237063.jpg"
            alt="IronCore Roofing contact background"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '2px' }}>
            Request a <span className="text-neon-green">Free Quote</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Fill out the form below and our team will get back to you within 24 hours. No obligation, no pressure.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-8 lg:px-16 py-20">
        <div className="grid lg:grid-cols-5 gap-16">

          {/* Left — Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Contact Information</h2>
              <p className="text-gray-500 text-sm leading-relaxed">
                Prefer to reach us directly? We're available by phone or email and happy to answer any questions.
              </p>
            </div>

            <div className="space-y-5">
              <a href="tel:717-280-2005" className="flex items-center gap-4 group cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-neon-green/10 rounded-xl group-hover:bg-neon-green/20 transition-colors">
                  <i className="ri-phone-fill text-neon-green text-xl"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Call Us</p>
                  <p className="text-gray-900 font-bold text-base group-hover:text-neon-green transition-colors">717-280-2005</p>
                </div>
              </a>

              <a href="mailto:Info@ironcoreroofingandexteriors.com" className="flex items-center gap-4 group cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-neon-green/10 rounded-xl group-hover:bg-neon-green/20 transition-colors">
                  <i className="ri-mail-fill text-neon-green text-xl"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Email Us</p>
                  <p className="text-gray-900 font-bold text-sm group-hover:text-neon-green transition-colors">Info@ironcoreroofingandexteriors.com</p>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-neon-green/10 rounded-xl flex-shrink-0">
                  <i className="ri-map-pin-fill text-neon-green text-xl"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Office</p>
                  <p className="text-gray-900 font-bold text-sm">2173 Embassy Dr.<br />Lancaster, PA 17603</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-neon-green/10 rounded-xl flex-shrink-0">
                  <i className="ri-time-fill text-neon-green text-xl"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Hours</p>
                  <p className="text-gray-900 font-bold text-sm">Mon–Fri: 7am – 6pm</p>
                  <p className="text-gray-500 text-xs">Sat: 8am – 2pm · Sun: Closed</p>
                </div>
              </div>
            </div>

            {/* Social */}
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-3">Follow Us</p>
              <div className="flex gap-3">
                <a href="https://www.facebook.com/ironcoreroofingandexteriors" target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg text-gray-500 hover:bg-neon-green/10 hover:text-neon-green transition-all cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href="https://www.instagram.com/ironcore_roofing_and_exteriors" target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg text-gray-500 hover:bg-neon-green/10 hover:text-neon-green transition-all cursor-pointer">
                  <i className="ri-instagram-fill text-lg"></i>
                </a>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3049.8!2d-76.3!3d40.04!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c624d0d0d0d0d0%3A0x0!2s2173%20Embassy%20Dr%2C%20Lancaster%2C%20PA%2017603!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="200"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="IronCore Roofing Office Location"
              ></iframe>
            </div>
          </div>

          {/* Right — Form */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full min-h-96 text-center py-16 bg-gray-50 rounded-2xl border border-gray-100">
                <div className="w-20 h-20 flex items-center justify-center bg-neon-green/10 rounded-full mb-6">
                  <i className="ri-check-double-line text-neon-green text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Message Sent!</h3>
                <p className="text-gray-500 max-w-sm text-sm leading-relaxed mb-8">
                  Thanks for reaching out! A member of our team will contact you within 24 hours to discuss your project.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="px-6 py-3 bg-neon-green text-gray-900 rounded-lg font-bold text-sm hover:bg-neon-green/80 transition-all cursor-pointer whitespace-nowrap"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-gray-50 rounded-2xl border border-gray-100 p-8 space-y-6"
              >
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-1">Send Us a Message</h2>
                  <p className="text-gray-400 text-sm">We'll respond within 24 hours.</p>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                      className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-neon-green transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-neon-green transition-colors"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(717) 555-0000"
                      className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-neon-green transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                      Service Needed <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-neon-green transition-colors cursor-pointer"
                    >
                      <option value="">Select a service...</option>
                      {services.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="Describe your project or issue — e.g., missing shingles after storm, need full roof replacement, etc."
                    className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-neon-green transition-colors resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</p>
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 border border-red-100 rounded-lg px-4 py-3">
                    <i className="ri-error-warning-line"></i>
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-neon-green text-gray-900 py-4 rounded-lg font-bold text-base hover:bg-neon-green/80 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill"></i>
                      Send My Request
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  Prefer to call? Reach us at{' '}
                  <a href="tel:717-280-2005" className="text-neon-green font-semibold hover:underline">717-280-2005</a>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      <Footer />
      <MobileCallButton />
    </div>
  );
}
